import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Box, Divider, Stack, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody,
  Paper, Alert, LinearProgress, Chip,
} from '@mui/material';
import { Upload as UploadIcon, CheckCircle as DoneIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import salesAPI from '../../api/sales';

// Simple CSV parser
function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  // Detect delimiter
  const firstLine = lines[0];
  const delimiter = firstLine.includes('\t') ? '\t' : ',';

  const headers = firstLine.split(delimiter).map((h) => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map((line) => {
    // Handle quoted fields
    const values = [];
    let cur = '';
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') { inQuote = !inQuote; }
      else if (c === delimiter && !inQuote) { values.push(cur.trim()); cur = ''; }
      else { cur += c; }
    }
    values.push(cur.trim());

    return headers.reduce((obj, h, i) => {
      obj[h] = values[i] ?? '';
      return obj;
    }, {});
  }).filter((row) => Object.values(row).some((v) => v !== ''));
}

export default function CSVImportDialog({ open, onClose, onImported }) {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setResult(null);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCSV(ev.target.result);
      if (!parsed.length) { toast.error('No data rows found in CSV'); return; }
      setHeaders(Object.keys(parsed[0]));
      setRows(parsed);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!rows.length) { toast.warning('No rows to import'); return; }
    setImporting(true);
    try {
      const res = await salesAPI.importCSV(rows);
      setResult({ imported: res.data.imported, batchId: res.data.batchId });
      toast.success(`${res.data.imported} leads imported!`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (result) onImported();
    else onClose();
    setRows([]);
    setHeaders([]);
    setFileName('');
    setResult(null);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle fontWeight={700}>📥 Import Leads from CSV</DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>

        {/* Success state */}
        {result && (
          <Box textAlign="center" py={4}>
            <DoneIcon sx={{ fontSize: 72, color: 'success.main' }} />
            <Typography variant="h5" fontWeight={700} mt={1}>Import Complete!</Typography>
            <Typography color="text.secondary">{result.imported} leads imported successfully</Typography>
            <Chip label={`Batch: ${result.batchId}`} sx={{ mt: 1 }} />
          </Box>
        )}

        {!result && (
          <Stack gap={2}>
            {/* Tips */}
            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
              <strong>CSV Tips:</strong> Column names are auto-detected. Supported fields include:
              businessName, firstName, lastName, email, phone, mobilePhone, state, monthlyRevenue,
              requestedAmount, industry, EIN, and more. Timezone is auto-assigned from the state column.
            </Alert>

            {/* File picker */}
            <Box
              sx={{
                border: '2px dashed', borderColor: 'primary.main', borderRadius: 2,
                p: 3, textAlign: 'center', cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => fileRef.current?.click()}
            >
              <UploadIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Typography fontWeight={600}>Click to select CSV or TSV file</Typography>
              <Typography variant="caption" color="text.secondary">
                {fileName || 'No file selected'}
              </Typography>
              <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" hidden onChange={handleFile} />
            </Box>

            {rows.length > 0 && (
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Preview: {rows.length} row{rows.length !== 1 ? 's' : ''} detected
                  </Typography>
                  <Chip label={`${headers.length} columns`} size="small" />
                </Stack>

                {importing && <LinearProgress sx={{ mb: 1 }} />}

                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto', maxHeight: 340 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>#</TableCell>
                        {headers.map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap', bgcolor: 'background.paper' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.slice(0, 20).map((row, i) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ color: 'text.disabled', fontSize: '0.72rem' }}>{i + 1}</TableCell>
                          {headers.map((h) => (
                            <TableCell key={h} sx={{ fontSize: '0.75rem', maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row[h]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
                {rows.length > 20 && (
                  <Typography variant="caption" color="text.secondary">Showing first 20 of {rows.length} rows</Typography>
                )}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={handleClose}>{result ? 'Done' : 'Cancel'}</Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={!rows.length || importing}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
          >
            {importing ? 'Importing…' : `Import ${rows.length} Lead${rows.length !== 1 ? 's' : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
